import TerminateContract from '../components/TerminateContract'
import React, { useState, useEffect } from 'react'
import Layout from '../components/Layout'
import api from '../services/api'
import { useParams, useNavigate } from 'react-router-dom'
import { useModal } from '../components/Modal'
import { useAuth } from '../context/AuthContext'

export default function ContractDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showAlert, showConfirm, ModalComponents } = useModal()
  const { user } = useAuth()
  const [contract, setContract] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [resending, setResending] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelError, setCancelError] = useState('')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchContract() }, [id])

  const fetchContract = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/contracts/${id}`)
      if (res.data.success) setContract(res.data.data)
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const handleCancelSubmit = async () => {
    setCancelError('')
    if (!cancelReason.trim()) { setCancelError('กรุณาระบุเหตุผลการยกเลิก'); return }
    const ok = await showConfirm('danger', 'ยืนยันการยกเลิกสัญญา', `ยืนยันการยกเลิกสัญญา ${contract.contract_no} ใช่หรือไม่?`)
    if (!ok) return
    try {
      await api.post(`/contracts/${contract.id}/cancel`, { reason: cancelReason })
      setShowCancelModal(false)
      showAlert('success', 'ยกเลิกสัญญาสำเร็จ', 'ยกเลิกสัญญาเรียบร้อยแล้ว')
      setTimeout(() => navigate('/contracts'), 1500)
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด') }
  }

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true)
      const res = await api.get(`/contracts/${contract.id}/pdf`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `contract_${contract.contract_no.replace(/\//g, '_')}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', 'ไม่สามารถดาวน์โหลด PDF ได้') }
    finally { setDownloading(false) }
  }

  const handleResendEmail = async () => {
    const ok = await showConfirm('warning', 'ยืนยันส่งอีเมลซ้ำ', `ส่ง PDF สัญญา ${contract.contract_no} ให้พนักงานอีกครั้งใช่หรือไม่?`)
    if (!ok) return
    try {
      setResending(true)
      const res = await api.post(`/contracts/${contract.id}/resend-email`)
      showAlert('success', 'ส่งอีเมลสำเร็จ', res.data.message)
      await fetchContract()
    } catch (err) {
      showAlert('error', 'ส่งอีเมลไม่สำเร็จ', err.response?.data?.message || 'กรุณาตรวจสอบการตั้งค่าอีเมล')
      await fetchContract()
    } finally { setResending(false) }
  }

  const statusConfig = {
    draft:           { label: 'แบบร่าง',          color: '#64748b', bg: '#f1f5f9' },
    pending:         { label: 'รอพนักงานเซ็น', color: '#f59e0b', bg: '#fef9c3' },
    signed:          { label: 'รอพยานลงนาม',   color: '#3b82f6', bg: '#eff6ff' },
    witness1_signed: { label: 'รอพยาน 2',       color: '#2563c5', bg: '#eff6ff' },
    approved:        { label: 'รอนายกลงนาม',   color: '#8b5cf6', bg: '#f5f3ff' },
    completed:       { label: 'เสร็จสมบูรณ์',  color: '#22c55e', bg: '#f0fdf4' },
    ended:           { label: 'สิ้นสุดก่อนกำหนด', color: '#ef4444', bg: '#fef2f2' },
    rejected:        { label: 'ยกเลิก',         color: '#ef4444', bg: '#fef2f2' },
  }

  if (loading) return <Layout><div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8' }}>กำลังโหลด...</div></Layout>
  if (!contract) return (
    <Layout>
      <div style={{ textAlign: 'center', padding: '80px' }}>
        <p style={{ color: '#94a3b8', marginBottom: '12px' }}>ไม่พบสัญญา</p>
        <button onClick={() => navigate('/contracts')} style={{ color: '#075dcc', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>กลับหน้าสัญญา</button>
      </div>
    </Layout>
  )

  const s = contract.status === 'completed' && contract.termination_date
    ? { label: 'กำหนดสิ้นสุด', color: '#2563eb', bg: '#eff6ff' }
    : statusConfig[contract.status] || { label: contract.status, color: '#64748b', bg: '#f1f5f9' }
  const empSig = contract.signatures?.find(sig => sig.signer_role === 'employee')

  const infoBlock = (title, fields) => (
    <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', padding: '20px 24px', marginBottom: '14px' }}>
      <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', marginBottom: '16px' }}>{title}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        {fields.map(([label, value, full]) => (
          <div key={label} style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>{label}</p>
            <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{value || '-'}</p>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <Layout>
      <ModalComponents />
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '500', color: '#0f172a' }}>รายละเอียดสัญญา</h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>เลขสัญญา {contract.contract_no}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: '500', padding: '4px 12px', borderRadius: '999px', color: s.color, background: s.bg }}>{s.label}</span>
            <button onClick={() => navigate('/contracts')} style={{ fontSize: '13px', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>← กลับ</button>
          </div>
        </div>

        {/* ข้อมูลพนักงาน */}
        {infoBlock('ข้อมูลพนักงาน', [
          ['ชื่อ-นามสกุล', `${contract.prefix}${contract.first_name} ${contract.last_name}`],
          ['เลขบัตรประชาชน', contract.id_card],
          ['กองงาน', contract.division_name],
          ['ตำแหน่ง', contract.position],
          ['ประเภทพนักงาน', contract.emp_type],
          ['โทรศัพท์', contract.phone],
          ['ที่อยู่', contract.address, true],
        ])}

        {/* ข้อมูลสัญญา */}
        <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', padding: '20px 24px', marginBottom: '14px' }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', marginBottom: '16px' }}>ข้อมูลสัญญา</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {[
              ['เลขสัญญา', contract.contract_no],
              ['ประเภทสัญญา', contract.contract_type],
              ['วันที่เริ่ม', new Date(contract.start_date).toLocaleDateString('th-TH')],
              ['วันที่สิ้นสุด', new Date(contract.end_date).toLocaleDateString('th-TH')],
              ['อัตราค่าตอบแทน', `${Number(contract.salary).toLocaleString('th-TH')} บาท`],
              ['เงินเพิ่มการครองชีพ', `${Number(contract.allowance || 0).toLocaleString('th-TH')} บาท`],
            ].map(([label, value]) => (
              <div key={label}>
                <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>{label}</p>
                <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{value || '-'}</p>
              </div>
            ))}
            <div style={{ gridColumn: 'span 2' }}>
              <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>รวมทั้งสิ้น</p>
              <p style={{ fontSize: '22px', fontWeight: '500', color: '#16a34a' }}>{Number(contract.total_salary).toLocaleString('th-TH')} บาท/เดือน</p>
            </div>
          </div>
        </div>

        {/* ลายเซ็น */}
        {(empSig || contract.witness1_sig || contract.witness2_sig || contract.mayor_sig) && (
          <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', padding: '20px 24px', marginBottom: '14px' }}>
            <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', marginBottom: '16px' }}>ลายเซ็น</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {empSig && (
                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>ลายเซ็นพนักงาน</p>
                  <img src={empSig.sig_data} alt="พนักงาน" style={{ maxHeight: '52px', border: '0.5px solid #e2e8f0', borderRadius: '6px', background: 'white', padding: '4px' }} />
                  <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>{new Date(empSig.signed_at).toLocaleString('th-TH')}</p>
                </div>
              )}
              {contract.witness1_sig && (
                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>พยานคนที่ 1{contract.witness1_name ? ` — ${contract.witness1_name}` : ''}</p>
                  <img src={contract.witness1_sig} alt="พยาน 1" style={{ maxHeight: '52px', border: '0.5px solid #e2e8f0', borderRadius: '6px', background: 'white', padding: '4px' }} />
                </div>
              )}
              {contract.witness2_sig && (
                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>พยานคนที่ 2{contract.witness2_name ? ` — ${contract.witness2_name}` : ''}</p>
                  <img src={contract.witness2_sig} alt="พยาน 2" style={{ maxHeight: '52px', border: '0.5px solid #e2e8f0', borderRadius: '6px', background: 'white', padding: '4px' }} />
                </div>
              )}
              {contract.mayor_sig && (
                <div style={{ background: '#fefce8', border: '0.5px solid #fde68a', borderRadius: '10px', padding: '12px' }}>
                  <p style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '8px' }}>ลายเซ็นนายกเทศมนตรี</p>
                  <img src={contract.mayor_sig} alt="นายก" style={{ maxHeight: '52px', border: '0.5px solid #fde68a', borderRadius: '6px', background: 'white', padding: '4px' }} />
                  {contract.mayor_signed_at && <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>{new Date(contract.mayor_signed_at).toLocaleString('th-TH')}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {contract.status === 'completed' && !contract.termination_date && (
          <div style={{ background: '#f8fafc', border: '0.5px solid #cbd5e1', borderRadius: '12px', padding: '14px 16px', marginBottom: '14px' }}>
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>สถานะเอกสารและการจัดส่ง</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
              <span>PDF ฉบับสมบูรณ์: <b>{contract.pdf_status === 'generated' ? 'สร้างสำเร็จ' : contract.pdf_status === 'failed' ? 'สร้างไม่สำเร็จ' : 'กำลังดำเนินการ'}</b></span>
              <span>อีเมล: <b>{contract.email_status === 'sent' ? 'ส่งสำเร็จ' : contract.email_status === 'failed' ? 'ส่งไม่สำเร็จ' : contract.email_status === 'skipped' ? 'ไม่มีอีเมลผู้รับ' : 'ยังไม่เสร็จ'}</b></span>
              <span>จำนวนครั้งที่ส่ง: <b>{contract.email_attempts || 0}</b></span>
              <span>ส่งล่าสุด: <b>{contract.email_sent_at ? new Date(contract.email_sent_at).toLocaleString('th-TH') : '-'}</b></span>
            </div>
            {contract.email_last_error && <p style={{ marginTop: '8px', fontSize: '11px', color: '#b91c1c' }}>รายละเอียด: {contract.email_last_error}</p>}
          </div>
        )}

        {/* เหตุผลยกเลิก */}
        {contract.status === 'rejected' && contract.employee_note && (
          <div style={{ background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: '12px', padding: '14px 16px', marginBottom: '14px' }}>
            <p style={{ fontSize: '11px', fontWeight: '500', color: '#ef4444', marginBottom: '4px' }}>เหตุผลการยกเลิก</p>
            <p style={{ fontSize: '13px', color: '#991b1b' }}>{contract.employee_note}</p>
          </div>
        )}

        {['hr','admin'].includes(user?.role) && <TerminateContract contract={contract} onSaved={fetchContract} confirm={showConfirm}/>}
        {['draft','pending'].includes(contract.status) && <button disabled={resending} onClick={async()=>{setResending(true);try{const res=await api.post(`/contracts/${contract.id}/send-qr-email`);showAlert('success','ส่งอีเมลแล้ว',res.data.message);await fetchContract()}catch(e){showAlert('error','ส่งไม่สำเร็จ',e.response?.data?.message || 'กรุณาลองใหม่')}finally{setResending(false)}}}>ส่ง E-mail ลิงก์ลงนามให้พนักงาน (15 วัน)</button>}
        {/* ปุ่ม */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/contracts')}
            style={{ flex: 1, padding: '10px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', minWidth: '120px' }}>
            กลับหน้าสัญญา
          </button>
          {contract.status === 'completed' && !contract.termination_date && (
            <button onClick={() => navigate(`/contracts/create?renew=${contract.id}&employee_id=${contract.employee_id}&salary=${contract.salary}&allowance=${contract.allowance || 0}&end_date=${contract.end_date}`)}
              style={{ flex: 1, padding: '10px', background: '#f97316', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: '120px' }}>
              <i className="ti ti-refresh" style={{ fontSize: '15px' }} />
              ต่อสัญญา
            </button>
          )}
          {contract.status === 'completed' && ['hr', 'admin'].includes(user?.role) && (
            <button onClick={handleResendEmail} disabled={resending}
              style={{ flex: 1, padding: '10px', background: resending ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: resending ? 'not-allowed' : 'pointer', minWidth: '140px' }}>
              {resending ? 'กำลังส่ง...' : contract.email_status === 'sent' ? 'ส่งอีเมลอีกครั้ง' : 'ส่งอีเมลสัญญาซ้ำ'}
            </button>
          )}
          {['completed', 'approved', 'ended'].includes(contract.status) && (
            <button onClick={handleDownloadPDF} disabled={downloading}
              style={{ flex: 1, padding: '10px', background: downloading ? '#86efac' : '#22c55e', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: downloading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: '120px' }}>
              <i className={`ti ${downloading ? 'ti-loader-2' : 'ti-download'}`} style={{ fontSize: '15px' }} />
              {downloading ? 'กำลังโหลด...' : 'ดาวน์โหลด PDF'}
            </button>
          )}
          {contract.status === 'pending' && (
            <button onClick={() => navigate(`/contracts/${contract.id}/qr`)}
              style={{ flex: 1, padding: '10px', background: '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', minWidth: '120px' }}>
              พิมพ์ QR Code
            </button>
          )}
          {['draft', 'pending', 'signed', 'witness1_signed'].includes(contract.status) && (
            <button onClick={() => { setCancelReason(''); setCancelError(''); setShowCancelModal(true) }}
              style={{ flex: 1, padding: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', minWidth: '120px' }}>
              ยกเลิกสัญญา
            </button>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '380px', overflow: 'hidden' }}>
            <div style={{ background: '#fef2f2', padding: '24px', textAlign: 'center' }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: '40px', color: '#ef4444', display: 'block', marginBottom: '10px' }} />
              <p style={{ fontSize: '16px', fontWeight: '500', color: '#991b1b' }}>ยกเลิกสัญญา</p>
              <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>สัญญา {contract.contract_no}</p>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '6px' }}>เหตุผลการยกเลิก <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder="ระบุเหตุผล..." rows={3}
                style={{ width: '100%', padding: '9px 12px', border: '0.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', resize: 'none', outline: 'none', fontFamily: 'inherit', marginBottom: '4px' }} />
              {cancelError && <p style={{ fontSize: '11px', color: '#ef4444', marginBottom: '12px' }}>{cancelError}</p>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                <button onClick={() => setShowCancelModal(false)}
                  style={{ flex: 1, padding: '10px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}>
                  ปิด
                </button>
                <button onClick={handleCancelSubmit}
                  style={{ flex: 1, padding: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                  ยืนยันยกเลิก
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
